import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { ClinicCard } from "./ClinicCard";
import { ClinicScheduleStatusDto } from "@/src/web-api-client";
import { ClinicUpdateModal } from "./ClinicUpdateModal";

interface ClinicListProps {
  clinics: ClinicScheduleStatusDto[];
  onClinicUpdate: (
    clinicId: number,
    payload: {
      file: File | null;
      data: ClinicForm;
      deleteCurrentImage: boolean;
    }
  ) => Promise<void>;
}

export function ClinicList({ clinics, onClinicUpdate }: ClinicListProps) {
  const [selectedClinic, setSelectedClinic] =
    useState<ClinicScheduleStatusDto | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const handleEdit = (clinic: ClinicScheduleStatusDto) => {
    setSelectedClinic(clinic);
    setIsModalOpen(true);
  };

  const handleClinicUpdate = async (
    file: File | null,
    data: ClinicForm,
    deleteCurrentImage: boolean
  ) => {
    if (!selectedClinic?.clinicId) return;

    await onClinicUpdate(selectedClinic.clinicId, {
      file,
      data,
      deleteCurrentImage,
    });
  };

  const filteredClinics = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return clinics;

    return clinics.filter((clinic) => {
      const name = clinic.clinicName?.toLowerCase() ?? "";
      const description = clinic.description?.toLowerCase() ?? "";
      return name.includes(q) || description.includes(q);
    });
  }, [clinics, keyword]);

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Tìm theo tên phòng khám hoặc mô tả"
            className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-800 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
          />
        </div>

        <div className="text-sm text-slate-500">
          Hiển thị{" "}
          <span className="font-semibold text-slate-800">
            {filteredClinics.length}
          </span>{" "}
          / {clinics.length}
        </div>
      </div>

      {filteredClinics.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
          <p className="text-base font-semibold text-slate-800">
            Không tìm thấy phòng khám phù hợp
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Hãy thử lại với từ khóa khác.
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
          data-testid="clinic-grid"
        >
          {filteredClinics.map((clinic) => (
            <ClinicCard
              key={clinic.clinicId}
              clinic={clinic}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}

      {selectedClinic && (
        <ClinicUpdateModal
          key={selectedClinic.clinicId}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedClinic(null);
          }}
          clinic={selectedClinic}
          onClinicUpdate={handleClinicUpdate}
        />
      )}
    </>
  );
}